import { Link, useLocation } from 'react-router-dom';
import { MapPin, Globe2, ArrowRight } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { BUSINESS } from '../data/business';
import { isEnglishPath } from '../utils/i18nRoutes';

export default function ServiceArea() {
  const { ref: cardRef, isVisible: cardVisible } = useScrollAnimation();
  const location = useLocation();
  const isEn = isEnglishPath(location.pathname);

  return (
    <section className="py-20 px-6 bg-white dark:bg-gray-900">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-dark dark:text-white mb-4">
            {isEn ? 'Location and Service Area' : 'Standort und Einsatzgebiet'}
          </h2>
          <p className="text-lg text-muted dark:text-gray-300 max-w-3xl mx-auto">
            {isEn
              ? 'OptiAI is headquartered in Vienna. We serve companies throughout Austria as well as in the rest of the DACH region – Germany and Switzerland. We handle most tasks remotely; for important meetings, we come to you.'
              : 'OptiAI hat seinen Sitz in Wien. Wir betreuen Unternehmen in ganz Österreich sowie im übrigen DACH-Raum – Deutschland und Schweiz. Die meisten Aufgaben erledigen wir remote; für wichtige Termine kommen wir zu Ihnen.'}
          </p>
        </div>

        <div
          ref={cardRef}
          className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
            cardVisible ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <div className="card-base p-8">
            <div className="w-12 h-12 bg-primary-tint rounded-xl flex items-center justify-center mb-5">
              <MapPin className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-bold text-dark dark:text-white mb-3">
              {isEn ? 'Vienna Office' : 'Standort Wien'}
            </h3>
            <p className="text-muted dark:text-gray-300 leading-relaxed mb-4 text-sm">
              {isEn
                ? `Our office is located in ${BUSINESS.address.locality}, ${BUSINESS.address.postalCode} ${BUSINESS.address.locality}, ${BUSINESS.address.country}. From here, we serve clients throughout the city and surrounding areas.`
                : `Unser Büro befindet sich in ${BUSINESS.address.locality}, ${BUSINESS.address.postalCode} ${BUSINESS.address.locality}, ${BUSINESS.address.country}. Von hier aus betreuen wir Kunden in der gesamten Stadt und im Umland.`}
            </p>
            <Link
              to={isEn ? '/en/ai-consulting-vienna' : '/ki-beratung-wien'}
              className="inline-flex items-center gap-2 text-primary dark:text-accent-light font-semibold text-sm hover:gap-3 transition-all"
            >
              {isEn ? 'AI Consulting in Vienna – details and directions' : 'KI-Beratung in Wien – Details und Anfahrt'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="card-base p-8">
            <div className="w-12 h-12 bg-primary-tint rounded-xl flex items-center justify-center mb-5">
              <Globe2 className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-bold text-dark dark:text-white mb-3">
              {isEn ? 'DACH Service Area' : 'Einsatzgebiet DACH'}
            </h3>
            <p className="text-muted dark:text-gray-300 leading-relaxed mb-4 text-sm">
              {isEn
                ? 'In addition to Austria, we serve clients in Germany and Switzerland. Work is done predominantly remotely; for kick-off meetings and important milestone dates, we travel on-site.'
                : 'Neben Österreich betreuen wir Kunden in Deutschland und der Schweiz. Die Arbeit erfolgt überwiegend remote; für Kick-off-Meetings und wichtige Meilensteintermine reisen wir an.'}
            </p>
            <div className="flex flex-wrap gap-2">
              {BUSINESS.areaServed.map((country) => (
                <span
                  key={country}
                  className="inline-block px-3 py-1.5 bg-primary-tint text-primary dark:text-accent-light text-sm rounded-full font-medium"
                >
                  {country}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
